import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { TeamService } from '@services/team.service';
import { OrgService } from '@services/org.service';
import { Team } from '@models/team.model';
import { Org } from '@models/org.model';
import { CmrService } from './cmr.service';
import { CmrConfig } from '../models/cmr-request.model';
import { emailsEqual } from '../utils/email.util';

function toArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

/**
 * Single source for CMR reference data (teams, orgs, component/distribution-list configs).
 *
 * Each list is fetched once and cached for the session via `shareReplay(1)`, so navigating
 * between CMR pages no longer re-fetches the same lookups. Admin screens that mutate this
 * data should call {@link invalidate} after a successful write so the next read re-fetches.
 *
 * The cached observables complete after their single emission (HTTP), so they are safe to
 * combine in `forkJoin`.
 *
 * Failures are never cached. `shareReplay({ refCount: false })` replays a terminal error to
 * every later subscriber, so without the `catchError` below a single transient 5xx would
 * break every CMR page for the rest of the session. Each stream instead drops its own cache
 * entry and emits an empty list, so the next read re-fetches and callers see the documented
 * "empty on failure" behaviour rather than an error.
 */
@Injectable({ providedIn: 'root' })
export class CmrDataFacade {
  private readonly teamService = inject(TeamService);
  private readonly orgService = inject(OrgService);
  private readonly cmrService = inject(CmrService);

  private teams$?: Observable<Team[]>;
  private orgs$?: Observable<Org[]>;
  private components$?: Observable<CmrConfig[]>;
  private distributionLists$?: Observable<CmrConfig[]>;

  /** All teams (active and inactive); callers filter as needed. Empty array on failure. */
  teams(): Observable<Team[]> {
    return (this.teams$ ??= this.teamService.getTeams().pipe(
      map((res) => (res.success ? toArray(res.result) : [])),
      catchError(() => this.uncache('teams$', [] as Team[])),
      shareReplay({ bufferSize: 1, refCount: false }),
    ));
  }

  /** All orgs. Empty array on failure. */
  orgs(): Observable<Org[]> {
    return (this.orgs$ ??= this.orgService.getOrgs().pipe(
      map((res) => (res.success && res.result ? toArray(res.result) : [])),
      catchError(() => this.uncache('orgs$', [] as Org[])),
      shareReplay({ bufferSize: 1, refCount: false }),
    ));
  }

  /** Component config entries (active and inactive); callers filter as needed. */
  components(): Observable<CmrConfig[]> {
    return (this.components$ ??= this.cmrService.getConfigs('component').pipe(
      map((res) => (res.success ? res.result : [])),
      catchError(() => this.uncache('components$', [] as CmrConfig[])),
      shareReplay({ bufferSize: 1, refCount: false }),
    ));
  }

  /** Distribution-list config entries. */
  distributionLists(): Observable<CmrConfig[]> {
    return (this.distributionLists$ ??= this.cmrService.getConfigs('distribution_list').pipe(
      map((res) => (res.success ? res.result : [])),
      catchError(() => this.uncache('distributionLists$', [] as CmrConfig[])),
      shareReplay({ bufferSize: 1, refCount: false }),
    ));
  }

  /**
   * Error handler for the cached streams: forgets this stream's cache entry so the next read
   * issues a fresh request, and recovers with `fallback` so subscribers get the documented
   * empty list instead of an error. Safe against the `??=` assignment racing it — HTTP always
   * errors asynchronously, so the field is already assigned by the time this runs.
   */
  private uncache<T>(key: 'teams$' | 'orgs$' | 'components$' | 'distributionLists$', fallback: T): Observable<T> {
    this[key] = undefined;
    return of(fallback);
  }

  /** Active teams the given user is a member of. */
  teamsForUser(email: string): Observable<Team[]> {
    return this.teams().pipe(
      map((teams) => teams.filter((t) => t.active && t.members?.some((m) => emailsEqual(m.email, email)))),
    );
  }

  /** Clears all cached reference data; the next read re-fetches. Call after admin writes. */
  invalidate(): void {
    this.teams$ = undefined;
    this.orgs$ = undefined;
    this.components$ = undefined;
    this.distributionLists$ = undefined;
  }
}
